import { RestError } from '@azure/core-http';
import { CourierListItemDto, KnownUserStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Container } from '@mui/material';
import { useMemo, useState } from 'react';
import { Paths, ProjectPathParams } from '../../../app/router/app.router';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { Snackbar } from '../../../ui/components/pure/snackbar/snackbar.component';
import { EditApprovalModal } from '../details/actions/edit-approval/edit-approval-action.component';
import { CouriersTable } from '../list/components/couriers-list-table.component';
import { useCouriersList } from '../list/hooks/use-couriers-list.hook';
import { CourierColumn } from '../list/hooks/use-couriers-table.hook';
import { CouriersListQueryInput } from '../list/logic/couriers-list-query.map';
import { makePendingCouriersPageHeaderProps, PENDING_COURIERS_EMPTY_MESSAGE } from './logic/pending-couriers-props.map';

// Module-level so the reference stays stable across renders.
const INTERNAL_GROUNDFLOOR = ['internal-groundfloor'];
const PENDING_COURIERS_QUERY: CouriersListQueryInput = { status: KnownUserStatus.Pending };
// Carrier groups load only when the carrier filter opens.
const PENDING_COURIERS_OPTIONS = { loadCarriersOnOpen: true };
// Every row is pending, so the status would say the same thing on each.
const HIDDEN_COLUMNS: CourierColumn[] = ['STATUS'];

/** The couriers list pinned to those awaiting approval, each reviewed from its row. */
export const PendingCouriersPage = ({ params }: { params: ProjectPathParams<Paths.pendingCouriers> }) => {
  const {
    project,
    result: couriers,
    infiniteScroll,
    formData,
    setFormData,
    carrierFilter
  } = useCouriersList(params.projectID, PENDING_COURIERS_QUERY, PENDING_COURIERS_OPTIONS);
  const [reviewing, setReviewing] = useState<CourierListItemDto | null>(null);
  const [succeeded, setSucceeded] = useState(false);
  const reviewAction = useMemo(
    () => ({ label: 'Review', onAction: setReviewing, availableUiFeatures: INTERNAL_GROUNDFLOOR }),
    []
  );

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );
  const error = project.error || couriers.error;

  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  const headerProps = makePendingCouriersPageHeaderProps(
    couriers.data?.pages[0].totalRecords || 0,
    formData,
    setFormData,
    carrierFilter
  );

  return (
    <Container>
      {project.data && <WindowTitle detail={project.data.name} pageTitle="Pending Couriers" />}
      <ListPageHeader {...headerProps} />
      <CouriersTable
        params={params}
        pages={couriers.data?.pages || []}
        infiniteScroll={infiniteScroll}
        setFormData={setFormData}
        sortData={sortData}
        isLoading={couriers.isLoading}
        isPreviousData={couriers.isPreviousData}
        hasError={couriers.isError}
        customMessage={PENDING_COURIERS_EMPTY_MESSAGE}
        hiddenColumns={HIDDEN_COLUMNS}
        rowAction={reviewAction}
      />
      {reviewing && (
        <EditApprovalModal
          courier={reviewing}
          onSuccess={() => {
            setReviewing(null);
            setSucceeded(true);
          }}
          onAbort={() => setReviewing(null)}
        />
      )}
      {succeeded && (
        <Snackbar severity="success" onClose={() => setSucceeded(false)}>
          The courier's approval has been updated
        </Snackbar>
      )}
    </Container>
  );
};
